/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { FunctionComponent } from 'react';
import type { CancelTokenSource } from 'axios';

import type {
  ApiResponseBase,
  OnboardingApplicationRequest,
  OnboardingLanguageRequest,
  MerchantThemeResponse,
  GenerateOTPBodyRequest,
  GenerateOTPResponse,
  AuthenticateUserBodyRequest,
  AuthenticateUserResponse,
  OnboardingProgressResponse,
  ApplicationReviewResponse,
  ApplicationFormsResponse,
  ApplicationDocumentsResponse,
  DocumentTemplateRequest,
  DocumentTemplateResponse,
  ApplicationFormRequest,
  ApplicationFormResponse,
  ApplicationDocumentRequest,
  ApplicationDocumentResponse,
  UploadDocumentBodyRequest,
  UploadDocumentResponse,
  SaveFormBodyRequest,
  HeartbeatFormBodyRequest,
  HeartbeatDocumentBodyRequest,
  AddCollaboratorBodyRequest,
  CollaboratorsRequest,
  CollaboratorsResponse,
  MerchantInfoResponse,
  ReInviteCollaboratorBodyRequest,
  ApplicationCardsResponse,
  SubmitApplicationResponse,
  AlphaVantageRequest,
  AlphaVantageResponse,
  LanguageResponse,
  GenerateOTPMerchantRequest,
  GenerateOTPMerchantResponse,
  AuthenticateUserMerchantRequest,
  AuthenticateUserMerchantResponse,
  MerchantApplicationsResponse,
  MerchantApplicationProgressRequest,
  MerchantApplicationProgressResponse,
  MerchantApplicationsRequest,
  RenewApplicationRequest,
  RenewApplicationResponse,
  SendReminderRequest,
  SendReminderResponse,
  SaveFormResponse,
} from './Onboarding.types';

import { OnboardingResponse } from './Onboarding.types';

import { createHttp } from '../../../services/http';
import { verifyResponseType } from './OnboardingApi.validation';

const createVoPayApi = (baseUrl: string) => {
  const http = createHttp(baseUrl);

  const api: OnboardingApi = {
    fetchMerchantTheme: async (params, source) => {
      const url = '/application/merchant/theme';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<MerchantThemeResponse>(url, data, possibleResTypes);
    },
    fetchMerchantInfo: async (bearer, source) => {
      const url = '/application/merchant';

      const { data } = await http.get<unknown>({
        url,
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<MerchantInfoResponse>(url, data, possibleResTypes);
    },
    fetchLanguage: async (params, source) => {
      const url = '/application/languages';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<LanguageResponse>(url, data, possibleResTypes);
    },
    fetchOnboardingProgress: async (bearer, source) => {
      const url = '/application/progress';

      const { data } = await http.get<unknown>({
        url,
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<OnboardingProgressResponse>(url, data, possibleResTypes);
    },
    fetchApplicationReview: async (params, bearer, source) => {
      const url = '/application/review';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApplicationReviewResponse>(url, data, possibleResTypes);
    },
    fetchApplicationForms: async (params, bearer, source) => {
      const url = '/application/forms';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApplicationFormsResponse>(url, data, possibleResTypes);
    },
    fetchApplicationForm: async (params, bearer, source) => {
      const url = '/application/form';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApplicationFormResponse>(url, data, possibleResTypes);
    },
    fetchApplicationDocuments: async (params, bearer, source) => {
      const url = '/application/documents';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApplicationDocumentsResponse>(url, data, possibleResTypes);
    },
    fetchApplicationDocument: async (params, bearer, source) => {
      const url = '/application/document';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApplicationDocumentResponse>(url, data, possibleResTypes);
    },
    fetchDocumentTemplate: async (params, bearer, source) => {
      const url = '/application/document/template';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<DocumentTemplateResponse>(url, data, possibleResTypes);
    },
    fetchCollaborators: async (params, bearer, source) => {
      const url = '/application/collaborators';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<CollaboratorsResponse>(url, data, possibleResTypes);
    },
    fetchApplicationCards: async (params, bearer, source) => {
      const url = '/application/cards';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApplicationCardsResponse>(url, data, possibleResTypes);
    },
    fetchAlphaVantage: async (params, source) => {
      const url = '/query?function=SYMBOL_SEARCH';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.AlphaVantageResponse];

      return verifyResponseType<AlphaVantageResponse>(url, data, possibleResTypes);
    },
    postGenerateOTP: async (params, body, source?) => {
      const url = '/application/otp';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        body: JSON.stringify(body),
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<GenerateOTPResponse>(url, data, possibleResTypes);
    },
    postAuthenticateUser: async (params, body, source?) => {
      const url = '/application/authenticate';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        body: JSON.stringify(body),
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.AuthenticateUserResponse];

      return verifyResponseType<AuthenticateUserResponse>(url, data, possibleResTypes);
    },
    postUploadDocument: async (body, bearer, source?) => {
      const url = '/application/document';

      const { data } = await http.post<unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<UploadDocumentResponse>(url, data, possibleResTypes);
    },
    postAddCollaborator: async (body, bearer, source?) => {
      const url = '/application/collaborator';

      const { data } = await http.post<unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApiResponseBase>(url, data, possibleResTypes);
    },
    postReInviteCollaborator: async (body, bearer, source?) => {
      const url = '/application/collaborator/invite';

      const { data } = await http.post<unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApiResponseBase>(url, data, possibleResTypes);
    },
    postSubmitApplication: async (body, bearer, source?) => {
      const url = '/application/submit';

      const { data } = await http.post<unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<SubmitApplicationResponse>(url, data, possibleResTypes);
    },
    putSaveForm: async (body, bearer, source?) => {
      const url = '/application/form/values';

      const { data } = await http.put<unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<SaveFormResponse>(url, data, possibleResTypes);
    },
    putHeartBeatForm: async (body, bearer, source?) => {
      const url = '/application/form/heartbeat';

      const { data } = await http.put<unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApiResponseBase>(url, data, possibleResTypes);
    },
    putHeartBeatApplication: async (bearer, source?) => {
      const url = '/application/heartbeat';

      const { data } = await http.put<unknown>({
        url,
        config: {},
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApiResponseBase>(url, data, possibleResTypes);
    },
    putHeartBeatDocument: async (body, bearer, source?) => {
      const url = '/application/document/heartbeat';

      const { data } = await http.put<unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
        bearer,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<ApiResponseBase>(url, data, possibleResTypes);
    },
    postGenerateMerchantOTP: async (body, source?) => {
      const url = '/user/otp';

      const { data } = await http.post<typeof body, unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<GenerateOTPMerchantResponse>(url, data, possibleResTypes);
    },
    postAuthenticateMerchantUser: async (body, source?) => {
      const url = '/user/authenticate';

      const { data } = await http.post<typeof body, unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<AuthenticateUserMerchantResponse>(url, data, possibleResTypes);
    },
    fetchMerchantApplications: async (params, source?) => {
      const url = '/merchant/applications';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<MerchantApplicationsResponse>(url, data, possibleResTypes);
    },
    fetchMerchantApplicationProgress: async (params, source?) => {
      const url = '/merchant/application/progress';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<MerchantApplicationProgressResponse>(url, data, possibleResTypes);
    },
    putRenewApplication: async (body, source?) => {
      const url = '/merchant/application';

      const { data } = await http.put<typeof body, unknown>({
        url,
        config: {},
        body: JSON.stringify(body),
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<RenewApplicationResponse>(url, data, possibleResTypes);
    },
    fetchMerchantApplicationReminder: async (params, source?) => {
      const url = '/merchant/application/login/url';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: OnboardingResponse[] = [OnboardingResponse.ApiResponse];

      return verifyResponseType<SendReminderResponse>(url, data, possibleResTypes);
    },
  };

  function withApiComponent<P>(
    C: FunctionComponent<Omit<P, 'api'> & { api: OnboardingApi }>,
  ): FunctionComponent<Omit<P, 'api'>> {
    return (props: Omit<P, 'api'>) => C({ ...props, api });
  }

  function withApiHook<P, R>(
    Hook: (hookProps: Omit<P, 'api'> & { api: OnboardingApi }) => R,
  ): (props: Omit<P, 'api'>) => R {
    return (props: Omit<P, 'api'>) => Hook({ ...props, api });
  }

  return {
    api,
    withApiComponent,
    withApiHook,
  };
};

export default createVoPayApi;

/* Alphabetical Order */

export type OnboardingApi = {
  fetchMerchantTheme: (
    params: OnboardingApplicationRequest,
    source?: CancelTokenSource,
  ) => Promise<MerchantThemeResponse>;

  fetchMerchantInfo: (bearer: string, source?: CancelTokenSource) => Promise<MerchantInfoResponse>;

  fetchLanguage: (params: OnboardingApplicationRequest, source?: CancelTokenSource) => Promise<LanguageResponse>;

  fetchOnboardingProgress: (bearer: string, source?: CancelTokenSource) => Promise<OnboardingProgressResponse>;

  fetchApplicationReview: (
    params: OnboardingLanguageRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApplicationReviewResponse>;

  fetchApplicationForms: (
    params: OnboardingLanguageRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApplicationFormsResponse>;

  fetchApplicationForm: (
    params: ApplicationFormRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApplicationFormResponse>;

  fetchApplicationDocuments: (
    params: OnboardingLanguageRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApplicationDocumentsResponse>;

  fetchApplicationDocument: (
    params: ApplicationDocumentRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApplicationDocumentResponse>;

  fetchDocumentTemplate: (
    params: DocumentTemplateRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<DocumentTemplateResponse>;

  fetchCollaborators: (
    params: CollaboratorsRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<CollaboratorsResponse>;

  fetchApplicationCards: (
    params: OnboardingLanguageRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApplicationCardsResponse>;

  fetchAlphaVantage: (params: AlphaVantageRequest, source?: CancelTokenSource) => Promise<AlphaVantageResponse>;

  postGenerateOTP: (
    params: OnboardingApplicationRequest,
    body: GenerateOTPBodyRequest,
    source?: CancelTokenSource,
  ) => Promise<GenerateOTPResponse>;

  postAuthenticateUser: (
    params: OnboardingApplicationRequest,
    body: AuthenticateUserBodyRequest,
    source?: CancelTokenSource,
  ) => Promise<AuthenticateUserResponse>;

  postUploadDocument: (
    body: UploadDocumentBodyRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<UploadDocumentResponse>;

  postAddCollaborator: (
    body: AddCollaboratorBodyRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponseBase>;

  postReInviteCollaborator: (
    body: ReInviteCollaboratorBodyRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponseBase>;

  postSubmitApplication: (
    body: OnboardingLanguageRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<SubmitApplicationResponse>;

  putSaveForm: (body: SaveFormBodyRequest, bearer: string, source?: CancelTokenSource) => Promise<SaveFormResponse>;

  putHeartBeatForm: (
    body: HeartbeatFormBodyRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponseBase>;

  putHeartBeatDocument: (
    body: HeartbeatDocumentBodyRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponseBase>;

  postGenerateMerchantOTP: (
    body: GenerateOTPMerchantRequest,
    source?: CancelTokenSource,
  ) => Promise<GenerateOTPMerchantResponse>;

  postAuthenticateMerchantUser: (
    body: AuthenticateUserMerchantRequest,
    source?: CancelTokenSource,
  ) => Promise<AuthenticateUserMerchantResponse>;
  fetchMerchantApplications: (
    params: MerchantApplicationsRequest,
    source?: CancelTokenSource,
  ) => Promise<MerchantApplicationsResponse>;
  fetchMerchantApplicationProgress: (
    params: MerchantApplicationProgressRequest,
    source?: CancelTokenSource,
  ) => Promise<MerchantApplicationProgressResponse>;
  putRenewApplication: (body: RenewApplicationRequest, source?: CancelTokenSource) => Promise<RenewApplicationResponse>;
  fetchMerchantApplicationReminder: (
    params: SendReminderRequest,
    source?: CancelTokenSource,
  ) => Promise<SendReminderResponse>;
  putHeartBeatApplication: (bearer: string, source?: CancelTokenSource) => Promise<ApiResponseBase>;
};
